import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { AlertCircle, ChevronDown, ChevronRight, RefreshCw, Search, Wand2, EyeOff, Layers, CheckCircle2, Hand } from 'lucide-react';
import legacyUapiService from '../services/LegacyUapiService';
import UapiPayloadWizard from './UapiPayloadWizard';
import UapiPayloadTree, { groupOperations } from './shared/UapiPayloadTree';
import Button from './shared/Button';
import Spinner from './shared/Spinner';
import CopyButton from './shared/CopyButton';
import './OhipTransactionsPage.css';
import './UniversalApiPage.css';
import './PaymentPorterProcessorPage.css';

// Admin > Platform > Universal API > Routes and Payloads.
//
// THE URL IS THE ROW. This screen listed payloads and showed which URLs each one served — a fair
// question, and not the one anyone actually has. What is asked is the reverse: here is a call we
// publish, does it have a body, and if not can we get one. So the URL leads and the payload is an
// attribute of it, which also turns the gap ("no body") into a visible row rather than an absence.
//
// MAPPING IS A BUTTON, NOT A WIZARD. Oracle publishes one document per path and our route tree mirrors
// those paths, so the body for `GET /act/v1/activities` is whatever act.json publishes at
// /act/v1/activities. Choosing it from a list of 117 was asking someone to re-derive a fact the spec
// already states. The server works it out; where it will not guess it says why, on the row, and the row
// stays unmapped rather than being filled with something plausible.
//
// SPOT. One document is stored once and shared by every URL that carries it — matched by path and then
// by content, since OHIP publishes the same shape at several paths. Redactions hang off the payload, so
// a duplicate would mean the same guest field redacted on one screen and exposed on another.
// Methods with no body to map. Not a preference — it is what the extractor can produce: a payload comes
// from a GET response or a write method's request body, and `applies_to` is built from get/post/put, so
// DELETE never appears in it and can never be matched. Listing those as "missing a body" made dozens of
// rows look like outstanding work that no amount of work would ever clear. Mirrors BODYLESS_METHODS in
// the legacy-uapi lambda; the two must agree or the counts here will not match what the bulk map does.
const BODYLESS_METHODS = ['DELETE'];
const carriesNoBody = (op) => BODYLESS_METHODS.includes(String(op.method || '').toUpperCase());

function UapiPayloadsPage() {
    const navigate = useNavigate();

    const [operations, setOperations] = useState([]);
    const [search, setSearch] = useState('');

    // WHICH MODULES ARE OPEN. Open by id, so everything starts shut: fifteen modules expanded is a page
    // that is scrolled rather than read, and the one you came for is somewhere inside it.
    const [expanded, setExpanded] = useState(() => new Set());
    const toggleModule = (module) => setExpanded(prev => {
        const next = new Set(prev);
        if (next.has(module)) next.delete(module); else next.add(module);
        return next;
    });
    const [filter, setFilter] = useState('all');     // all | mapped | unmapped
    const [loading, setLoading] = useState(true);
    const [mapping, setMapping] = useState(null);    // the operation currently being mapped
    const [bulk, setBulk] = useState(false);
    // The URL the manual wizard is open for. Auto-mapping refuses whenever the spec does not state the
    // answer — a body published at a different path, or under a method the spec does not declare — and
    // those are exactly the cases a person can resolve by looking. The wizard is the one we already had;
    // opening it aimed at a URL (forOperation) preselects that URL's spec and pre-ticks the URL itself.
    const [manual, setManual] = useState(null);
    const [error, setError] = useState(null);
    const [notice, setNotice] = useState(null);
    // Per-row refusals from the last attempt. Kept on screen and keyed by operation, because the reason
    // is the useful half of the feature — a toast that vanishes answers nothing.
    const [reasons, setReasons] = useState({});

    const errText = (e, fallback) =>
        e?.response?.data?.error?.message || e?.response?.data?.message || e?.message || fallback;

    const load = useCallback(async () => {
        try {
            setLoading(true);
            setError(null);
            const res = await legacyUapiService.listOperationMapping();
            setOperations((res.data || res).operations || []);
        } catch (e) {
            setError(errText(e, 'Failed to load the Universal API operations'));
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { load(); }, [load]);

    const mapOne = async (op) => {
        setMapping(op.id);
        setError(null);
        try {
            const res = await legacyUapiService.automapOperation(op.id);
            const d = res.data || res;
            if (d.ok) {
                setReasons(prev => { const n = { ...prev }; delete n[op.id]; return n; });
                setNotice(`${op.method} ${op.full_path} → "${d.payloadName}"`
                    + (d.created ? '' : ' — existing body reused, one row'));
                await load();
            } else {
                setReasons(prev => ({ ...prev, [op.id]: d.reason }));
            }
        } catch (e) {
            setError(errText(e, 'Could not map that URL'));
        } finally {
            setMapping(null);
        }
    };

    const mapAll = async () => {
        setBulk(true);
        setError(null);
        setNotice(null);
        try {
            const res = await legacyUapiService.automapAll();
            const d = res.data || res;
            const failed = {};
            for (const r of (d.results || [])) if (!r.ok) failed[r.operationId] = r.reason;
            setReasons(failed);
            setNotice(`Mapped ${d.mapped} of ${d.considered}.`
                + (d.skipped ? ` ${d.skipped} could not be — the reason is on each row.` : ''));
            await load();
        } catch (e) {
            setError(errText(e, 'Could not run the bulk mapping'));
        } finally {
            setBulk(false);
        }
    };

    // Counted separately: a URL with no body is work for THIS screen, a route with no operations is work
    // for the Universal API page. Lumping them together would make "Map all missing" look like it had
    // failed on rows it was never able to touch.
    const unmappedCount = useMemo(
        () => operations.filter(o => o.method && !o.payload_id && !carriesNoBody(o)).length, [operations]);
    const emptyRouteCount = useMemo(() => operations.filter(o => !o.method).length, [operations]);

    // How many URLs each payload serves. Shown on the row so a shared body reads as deliberate rather
    // than looking like a duplicate someone should clean up.
    const shareCount = useMemo(() => {
        const c = {};
        for (const o of operations) if (o.payload_id) c[o.payload_id] = (c[o.payload_id] || 0) + 1;
        return c;
    }, [operations]);

    const shown = operations.filter(o => {
        if (filter === 'mapped' && !o.payload_id) return false;
        if (filter === 'unmapped' && (o.payload_id || !o.method || carriesNoBody(o))) return false;
        if (filter === 'empty' && o.method) return false;
        const q = search.trim().toLowerCase();
        if (!q) return true;
        return [o.full_path, o.method, o.description, o.payload_name, o.spec_file, o.module_path]
            .some(v => String(v || '').toLowerCase().includes(q));
    });

    // ── Route -> payload -> the URLs that carry it ──────────────────────────────────────────────────
    //
    // The flat list could not show that one document serves several URLs: each URL was its own row and
    // the only hint was a chip that (because content matching was broken) never appeared. Nesting the
    // URLs UNDER the payload makes the relationship the structure of the page rather than a label on it —
    // a payload with three URLs is three lines under one name, which cannot be missed or misread.
    const grouped = useMemo(() => groupOperations(shown), [shown]);

    // Declared AFTER `grouped`, which it reads. A const used above its own declaration crashes at render
    // with a message naming the variable and not the line that moved.
    const allExpanded = grouped.length > 0 && grouped.every(g => expanded.has(g.module));

    return (
        <div className="otx-page">
            <div className="otx-header">
                <div>
                    <h1>Routes and Payloads</h1>
                    <p className="otx-subtitle">
                        Every URL the Universal API publishes, and the message body it carries. One document
                        is shared by every URL that carries it, so redactions are stated once.
                    </p>
                </div>
                <div className="otx-header-actions">
                    {/* One control for the whole page. With 15 modules, folding them one at a time to
                        find the one you want is the work the chevrons were meant to save. */}
                    <Button
                        variant="secondary"
                        onClick={() => setExpanded(allExpanded ? new Set() : new Set(grouped.map(g => g.module)))}
                        disabled={loading || !grouped.length}
                    >
                        {allExpanded
                            ? <ChevronRight className="icon" />
                            : <ChevronDown className="icon" />}
                        {allExpanded ? 'Collapse all' : 'Expand all'}
                    </Button>
                    <Button variant="secondary" onClick={mapAll} disabled={bulk || loading || !unmappedCount}>
                        <Wand2 className="icon" />
                        {bulk ? 'Mapping…' : `Map all missing${unmappedCount ? ` (${unmappedCount})` : ''}`}
                    </Button>
                    <Button variant="success" onClick={load} disabled={loading || bulk}>
                        <RefreshCw className="icon" /> Refresh
                    </Button>
                </div>
            </div>

            {manual && (
                <UapiPayloadWizard
                    forOperation={manual}
                    onClose={() => setManual(null)}
                    onSaved={(d) => {
                        setManual(null);
                        setReasons(prev => { const n = { ...prev }; delete n[manual.id]; return n; });
                        setNotice(`${manual.method} ${manual.full_path} → "${d.payload.name}" (mapped by hand).`);
                        load();
                    }}
                />
            )}

            {notice && <p className="lu-payload-notice">{notice}</p>}

            {error && (
                <div className="error-alert">
                    <div className="error-alert-content">
                        <AlertCircle className="error-alert-icon" />
                        <p className="error-alert-message">{error}</p>
                    </div>
                </div>
            )}

            {!loading && operations.length > 0 && (
                <div className="uapi-search-row uapi-page-search">
                    <Search size={15} className="uapi-search-icon" />
                    <input
                        type="text"
                        className="pst-hotel-search"
                        placeholder="Search by URL, method, payload, spec…"
                        value={search}
                        onChange={e => setSearch(e.target.value)}
                    />
                    <div className="uapi-filter-chips">
                        {[['all', `All (${operations.length})`],
                          ['unmapped', `No body (${unmappedCount})`],
                          ['mapped', `Mapped (${operations.filter(o => o.payload_id).length})`],
                          ...(emptyRouteCount ? [['empty', `No operations (${emptyRouteCount})`]] : []),
                         ].map(([k, label]) => (
                            <button
                                key={k}
                                type="button"
                                className={`uapi-filter-chip${filter === k ? ' on' : ''}`}
                                onClick={() => setFilter(k)}
                            >
                                {label}
                            </button>
                        ))}
                    </div>
                </div>
            )}

            {loading ? (
                <div className="loading-section"><Spinner size="md" /><p>Loading operations...</p></div>
            ) : operations.length === 0 ? (
                <div className="otx-table-wrap uapi-empty-state">
                    <p className="uapi-empty-title">The Universal API publishes no operations yet.</p>
                    <p className="uapi-empty-body">
                        Import them from Oracle's spec on the Universal API page first — this screen maps
                        bodies to URLs that already exist.
                    </p>
                </div>
            ) : (
                <>
                <UapiPayloadTree
                    groups={grouped}
                    expanded={expanded}
                    onToggleModule={toggleModule}
                    renderPayloadActions={(pl) => (
                        /* Redactions sit on the PAYLOAD, not on a URL — one document, one set of
                           rules, which is the whole point of sharing it. */
                        <button
                            type="button"
                            className="ppp-detail-btn"
                            onClick={() => navigate(`/admin/universal-api/payloads/${pl.payload_id}/redactions`)}
                            title="What to strip from this body before it leaves the Universal API"
                        >
                            <EyeOff size={13} /> Redactions
                        </button>
                    )}
                    renderUnmapped={(o) => {
                        const reason = reasons[o.id];
                        const bodyless = carriesNoBody(o);
                        return (
                            <div key={o.id || o.full_path}
                                 className={`uapi-pl-row uapi-pl-row-none${bodyless ? ' bodyless' : ''}`}>
                                <div className="uapi-pl-name">
                                    {o.method
                                        ? (bodyless
                                            ? <span className="uapi-no-body-expected">No body expected</span>
                                            : <span className="uapi-override-none">No payload</span>)
                                        : <span className="uapi-override-none">No operations yet</span>}
                                    {reason && (
                                        <span className="uapi-map-reason">
                                            <AlertCircle size={12} /> {reason}
                                        </span>
                                    )}
                                </div>
                                <div className="uapi-pl-urls">
                                    <div className="uapi-url-line">
                                        {o.method
                                            ? <span className={`lu-method lu-method-${String(o.method).toLowerCase()}`}>
                                                  {o.method}
                                              </span>
                                            : <span className="uapi-route-chip">route</span>}
                                        <code className="lu-mono uapi-payload-path">{o.full_path}</code>
                                        <span className="uapi-url-actions">
                                            {!o.method ? (
                                                <button type="button" className="ppp-detail-btn"
                                                        onClick={() => navigate('/admin/universal-api')}>
                                                    Add operations
                                                </button>
                                            ) : (<>
                                                {!bodyless && (
                                                    <button
                                                        type="button"
                                                        className="ppp-detail-btn ppp-detail-btn-primary"
                                                        onClick={() => mapOne(o)}
                                                        disabled={mapping === o.id || bulk}
                                                        title="Work out this URL's body from Oracle's spec and attach it"
                                                    >
                                                        <Wand2 size={13} />
                                                        {mapping === o.id ? 'Mapping…' : reason ? 'Retry' : 'Create or add to payload'}
                                                    </button>
                                                )}
                                                <button
                                                    type="button"
                                                    className="ppp-detail-btn"
                                                    onClick={() => setManual(o)}
                                                    disabled={bulk || mapping === o.id}
                                                    title="Choose the body for this URL yourself, from Oracle's spec"
                                                >
                                                    <Hand size={13} /> Manual
                                                </button>
                                            </>)}
                                        </span>
                                    </div>
                                </div>
                            </div>
                        );
                    }}
                />
                    {grouped.length === 0 && (
                        <div className="otx-table-wrap uapi-empty-state">
                            <p className="uapi-empty-title">Nothing matches that.</p>
                        </div>
                    )}
                </>
            )}
        </div>
    );
}

export default UapiPayloadsPage;
